import { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { App, Descriptions, Row } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import { useStore } from '@hub/helpers/use-store';
import { formatExpirationDate } from '@monorepo/client-common';
import { Card, Statistic, TextComponent } from '@monorepo/react-components';
import { Account, AccountStateKey, AccountStatus } from '@monorepo/types';
import TradersLogin from '../../../traders-login/traders-login';
import AccountConsistencyRules from '../account-consistency-rules/account-consistency-rules';

interface Props {
  account: Account;
  programId: string;
  buyingPowerText: string;
}

const AccountOverview: FunctionComponent<Props> = ({
  account,
  programId,
  buyingPowerText,
}) => {
  const userId = account.tsUserId || (account.tsAccountId as string);
  const accountId = account.tsAccountId as string;
  const displayName = account.tsAccount || accountId;

  const {
    dataStore: { programStore },
  } = useStore();
  const { t } = useTranslation(['dashboardTemplates', 'common']);
  const { modal } = App.useApp();

  const program = programStore.get(programId);

  const openTradersLoginModal = () => {
    modal.info({
      maskClosable: true,
      title: t('dashboardTemplates:account_overview.login_credentials'),
      content: (
        <TradersLogin userId={userId} tradingSystem={account.tradingSystem} />
      ),
    });
  };

  const getDisabledReason = () => {
    if (account.status === AccountStatus.Active) {
      return null;
    }

    let reasonText = '';

    if (account.status === AccountStatus.WaitingForRisk) {
      reasonText = t('dashboardTemplates:account_overview.waiting_for_review');
    }

    const isTarget = account.state[AccountStateKey.Target] || false;
    const isDailyPause = account.state[AccountStateKey.DailyPause] || false;
    const isDailyLoss = account.state[AccountStateKey.DailyLoss] || false;
    const isExpired = account.state[AccountStateKey.Expired] || false;
    const isTerminated = account.state[AccountStateKey.Terminated] || false;

    if (isExpired) {
      reasonText = t('dashboardTemplates:account_overview.expired');
    }
    if (isTarget) {
      reasonText = t('dashboardTemplates:account_overview.reached_target');
    }
    if (isDailyLoss) {
      reasonText = t('dashboardTemplates:account_overview.reached_daily_loss');
    }
    if (isDailyPause) {
      reasonText = t('dashboardTemplates:account_overview.reached_daily_pause');
    }
    if (isTerminated) {
      reasonText = t('dashboardTemplates:account_overview.reached_stopout');
    }

    return (
      <Descriptions.Item
        label={t('dashboardTemplates:account_overview.disable_reason')}
      >
        {reasonText}
      </Descriptions.Item>
    );
  };

  return (
    <Card
      title={t('dashboardTemplates:account_overview.account_overview_title')}
      bordered={true}
    >
      <Descriptions column={1}>
        <Descriptions.Item label={t('common:login')}>
          {displayName}
        </Descriptions.Item>
        <Descriptions.Item
          label={t('dashboardTemplates:account_overview.account_credentials')}
        >
          <EyeOutlined onClick={openTradersLoginModal} />
        </Descriptions.Item>
        <Descriptions.Item label={t('dashboardTemplates:program')}>
          {program?.name}
        </Descriptions.Item>
        <Descriptions.Item label={buyingPowerText}>
          <Statistic
            value={program?.config.buyingPower}
            precision={0}
            prefix={'$'}
          />
        </Descriptions.Item>
        <Descriptions.Item
          label={t('dashboardTemplates:account_overview.account_status')}
        >
          <TextComponent capitalize={true}>{account.status}</TextComponent>
        </Descriptions.Item>
        {getDisabledReason()}

        {account?.expireDate ? (
          <Descriptions.Item label={t('dashboardTemplates:expiration_date')}>
            {formatExpirationDate(account.expireDate)}
          </Descriptions.Item>
        ) : null}
      </Descriptions>
      <Row>
        <AccountConsistencyRules
          accountId={accountId}
          programId={account.programId}
        />
      </Row>
    </Card>
  );
};

export default AccountOverview;
